import type { ServiceNowEnrichment } from './types';

export const apexClassServiceNowMapping: Record<string, ServiceNowEnrichment> = {
  'AccountContactRolesCont.ApexClass': {
    name: 'AccountContactRolesCont',
    dependencyType: 'Visualforce Controller',
    overview: 'Allows creating Contact Roles on Accounts from Contact page via button action',
    serviceNowConcept: 'UI Action + Server Script',
    serviceNowMapping: [
      'Custom UI Action on Contact triggering GlideRecord insert to Account Contact Role table',
    ],
    migrationConsiderations: [
      'Need to create equivalent junction table in ServiceNow',
      'Consider using Related Lists',
    ],
    status: 'Active',
    userStory: {
      description:
        'As a sales rep, I need to assign Contact Roles to Accounts from a Contact record, so that I can track relationships between contacts and their associated accounts.',
      behavior: [
        'Trigger: User clicks a button from a Contact page to add an Account Contact Role',
        'Actions: Creates a new AccountContactRole linking the Contact to a selected Account with a specified Role; redirects back to Contact after save',
        'Conditions: Contact ID passed via URL parameter; supports "Save" and "Save & New" operations',
      ],
      data: [
        'Fields: ContactId, AccountId, Role (on AccountContactRole)',
        'Related Objects: Contact, Account, AccountContactRole',
      ],
    },
  },
  'AccountContactRolesController_Lightning.ApexClass': {
    name: 'AccountContactRolesController_Lightning',
    dependencyType: 'Lightning Controller',
    overview: 'Displays paginated list of Account Contact Roles for a Contact in Lightning UI',
    serviceNowConcept: 'Widget + REST API',
    serviceNowMapping: ['Service Portal Widget with GlideAggregate for pagination'],
    migrationConsiderations: [
      'Requires equivalent data model',
      'Implement pagination in client script',
    ],
    status: 'Active',
    userStory: {
      description:
        "As a sales rep viewing a Contact in Lightning, I need to see all Account Contact Roles for that Contact, so that I can understand the Contact's relationships across multiple Accounts.",
      behavior: [
        'Trigger: User views Contact record page (embedded Lightning Visualforce component)',
        'Actions: Displays list of Account Contact Roles showing Account Name, Type, Hierarchy Level, Billing Address, Role, and Primary flag; supports pagination (show 5 initially, then "Show More")',
        'Conditions: Filtered by current Contact; sorted by Account Name; Lightning-compatible',
      ],
      data: [
        'Fields: Account.Name, Account.Type, Account.IM_Hierarchy_Level__c, Account.BillingStreet/City/PostalCode, Role, IsPrimary',
        'Related Objects: Contact, AccountContactRole, Account',
      ],
    },
  },
  'AccountContactRolesController.ApexClass': {
    name: 'AccountContactRolesController',
    dependencyType: 'Visualforce Controller',
    overview: 'Classic UI version - displays Account Contact Roles for a Contact',
    serviceNowConcept: 'UI Page + Script Include',
    serviceNowMapping: ['Jelly-based UI Page calling Script Include for data'],
    migrationConsiderations: [
      'Consider consolidating with Lightning version in single ServiceNow widget',
    ],
    status: 'Active',
    userStory: {
      description:
        "As a sales rep viewing a Contact in Classic, I need to see all Account Contact Roles for that Contact, so that I can understand the Contact's relationships across multiple Accounts.",
      behavior: [
        'Trigger: User views Contact record page (embedded Visualforce component)',
        'Actions: Displays list of Account Contact Roles showing Account Name, Type, Hierarchy Level, Billing Address, Role, and Primary flag; supports pagination (show 5 initially, then "Show More")',
        'Conditions: Filtered by current Contact; sorted by Account Name; Classic UI version',
      ],
      data: [
        'Fields: Account.Name, Account.Type, Account.IM_Hierarchy_Level__c, Account.BillingStreet/City/PostalCode, Role, IsPrimary',
        'Related Objects: Contact, AccountContactRole, Account',
      ],
    },
  },
  'AccountCoverageFieldsSync.ApexClass': {
    name: 'AccountCoverageFieldsSync',
    dependencyType: 'Batch Class',
    overview: 'Syncs formula field values to lookup fields on Accounts for rollup compatibility',
    serviceNowConcept: 'Scheduled Script Execution',
    serviceNowMapping: ['Scheduled Job calling Script Include to copy calculated → lookup fields'],
    migrationConsiderations: [
      'May not be needed if ServiceNow calculated fields support rollups natively',
    ],
    status: 'Active',
    userStory: {
      description:
        'As a data administrator, I need formula field values automatically synced to corresponding lookup fields on Accounts, so that rollup summaries and reports work correctly on non-formula fields.',
      behavior: [
        'Trigger: Executed by AccountCoverageFieldValuesScheduler or manually',
        'Actions: Queries all Accounts; copies values from formula fields to lookup fields based on field mappings in AccountCoverageFieldsSync__c custom setting; sends completion email with record counts',
        "Conditions: Skip copy for Sector/Industry fields when Industry_Override__c = true AND IM_Hierarchy_Level = 'Country Parent'",
      ],
      data: [
        'Fields: Mapped via AccountCoverageFieldsSync__c custom setting (From_Field__c → To_Field__c), Industry_Override__c, IM_Hierarchy_Level__c',
        'Related Objects: Account, AccountCoverageFieldsSync__c (Custom Setting)',
      ],
    },
  },
  'AccountCoverageFieldsSyncTest.ApexClass': {
    name: 'AccountCoverageFieldsSyncTest',
    dependencyType: 'Test Class',
    overview: 'Unit tests for AccountCoverageFieldsSync batch',
    serviceNowConcept: 'ATF Test',
    serviceNowMapping: ['Automated Test Framework test for scheduled sync job'],
    migrationConsiderations: ['Create equivalent ATF tests', 'Consider test data strategy'],
    status: 'Test Class',
    userStory: {
      description: 'ℹ️ TEST CLASS - No functional migration needed',
      purpose: 'Provides unit test coverage for AccountCoverageFieldsSync batch class.',
      testCoverageFor: 'AccountCoverageFieldsSync',
      note: 'ServiceNow will need equivalent test coverage for whatever automation replaces the AccountCoverageFieldsSync functionality.',
    },
  },
  'AccountCoverageFieldValuesScheduler.ApexClass': {
    name: 'AccountCoverageFieldValuesScheduler',
    dependencyType: 'Schedulable',
    overview: 'Schedules AccountCoverageFieldsSync batch to run periodically',
    serviceNowConcept: 'Scheduled Job',
    serviceNowMapping: ['sys_trigger record with cron schedule'],
    migrationConsiderations: [
      'Simple scheduler migration',
      'Configure run frequency in ServiceNow',
    ],
    status: 'Active',
    userStory: {
      description:
        'As a data administrator, I need the Account Coverage Field Sync to run on a schedule, so that formula-to-lookup field syncing happens automatically without manual intervention.',
      behavior: [
        'Trigger: Scheduled job execution (cron-based)',
        'Actions: Executes AccountCoverageFieldsSync batch with filtered query for Accounts that need syncing',
        "Conditions: Only processes Accounts where AccountCoverageUpdated1__c = true OR AccountCoverageUpdated2__c = true OR Sync_fields__c = true; AND IM_Hierarchy_Level__c IN ('Sales (Site)', 'Country Parent'); batch size = 50",
      ],
      data: [
        'Fields: AccountCoverageUpdated1__c, AccountCoverageUpdated2__c, Sync_fields__c, IM_Hierarchy_Level__c',
        'Related Objects: Account',
      ],
    },
  },
  'AccountHelper.ApexClass': {
    name: 'AccountHelper',
    dependencyType: 'Trigger Helper',
    overview:
      'Validates/enriches Account data - country validation, postal code lookup, profile restrictions',
    serviceNowConcept: 'Business Rule(s)',
    serviceNowMapping: ['Multiple Before Insert/Update Business Rules on Account table'],
    migrationConsiderations: [
      'Split into separate Business Rules by function',
      'Consider ACLs for profile restrictions',
    ],
    status: 'Active',
    userStory: {
      description:
        'As a system, I need to validate and enrich Account data on insert/update, so that data integrity rules are enforced and related fields are auto-populated.',
      behavior: [
        'Trigger: Called by AccountTriggerHandler on Account insert/update/delete',
        'Actions:',
        'Validates Billing Country against approved country list and translates to standard name',
        'Auto-populates Postal_Code2__c lookup based on BillingPostalCode + BillingCountry match',
        'Sets QTB_Payable_Customer_Model__c = true for specific country/hierarchy combinations',
        'Sets Registration_Number_Updated__c flag when Registration Number changes',
        'Prevents creation by non-admin profiles (except during Lead Conversion)',
        'Blocks edits/deletes to "Dummy Account"',
        'Conditions: Record type must be Standard_Account for some rules; profile-based restrictions from custom labels',
      ],
      data: [
        'Fields: BillingCountry, BillingPostalCode, Postal_Code2__c, IM_Hierarchy_Level__c, QTB_Payable_Customer_Model__c, Registration_Number__c, Registration_Number_Updated__c, IM_Lead_Conversion_Flag__c',
        'Related Objects: Account, Postal_Code__c, IM_Country__c, QTB_Billing_Countries__c (Custom Setting)',
      ],
    },
  },
  'AccountPrioritizationBatch.ApexClass': {
    name: 'AccountPrioritizationBatch',
    dependencyType: 'Batch Class (Deprecated)',
    overview: 'DEPRECATED - Was for IMGA account prioritization',
    serviceNowConcept: 'N/A',
    serviceNowMapping: ['N/A - Do not migrate'],
    migrationConsiderations: ['Skip migration - code is commented out'],
    status: 'Deprecated',
    userStory: {
      description:
        '⚠️ DEPRECATED - Code is commented out\n\nAs a sales rep, I need my IMGA accounts automatically prioritized based on volume thresholds and risk profiles, so that I focus on the highest-value opportunities.',
      behavior: ['Trigger: N/A (deprecated)', 'Actions: N/A', 'Conditions: N/A'],
      data: ['Fields: N/A', 'Related Objects: N/A'],
    },
  },
  'AccountPrioritizationService.ApexClass': {
    name: 'AccountPrioritizationService',
    dependencyType: 'Service Class',
    overview: 'DEPRECATED - Business logic for account prioritization',
    serviceNowConcept: 'N/A',
    serviceNowMapping: ['N/A - Do not migrate'],
    migrationConsiderations: ['Skip migration - code is commented out'],
    status: 'Deprecated',
    userStory: {
      description:
        '⚠️ DEPRECATED - Code is commented out\n\nAs a system, I need business logic for account prioritization calculations, so that the batch job can determine which accounts to prioritize.',
      behavior: ['Trigger: N/A (deprecated)', 'Actions: N/A', 'Conditions: N/A'],
      data: ['Fields: N/A', 'Related Objects: N/A'],
    },
  },
  'AccountRegNoUpdate_Scheduler.ApexClass': {
    name: 'AccountRegNoUpdate_Scheduler',
    dependencyType: 'Schedulable',
    overview: 'Schedules registration number sync batch',
    serviceNowConcept: 'Scheduled Job',
    serviceNowMapping: ['sys_trigger record executing Script Include'],
    migrationConsiderations: ['Simple scheduler', 'Configure frequency'],
    status: 'Active',
    userStory: {
      description:
        'As a data administrator, I need the Account Registration Number sync to run on a schedule, so that registration numbers are automatically propagated to related records.',
      behavior: [
        'Trigger: Scheduled job execution (cron-based)',
        'Actions: Executes AccountRegNumberUpdateBatch with batch size of 50',
        'Conditions: None - simply kicks off the batch',
      ],
      data: ['Fields: N/A (scheduling only)', 'Related Objects: Account'],
    },
  },
  'AccountRegNumberUpdateBatch.ApexClass': {
    name: 'AccountRegNumberUpdateBatch',
    dependencyType: 'Batch Class',
    overview:
      'Propagates Account Registration Number to Contacts, Opportunities, Customer Accounts',
    serviceNowConcept: 'Scheduled Script + Business Rule',
    serviceNowMapping: ['After Update BR on Account to flag', 'Scheduled job to propagate'],
    migrationConsiderations: ['Consider real-time sync via Business Rule instead of batch'],
    status: 'Active',
    userStory: {
      description:
        'As a data administrator, I need Account Registration Numbers automatically propagated to related Contacts, Opportunities, and Customer Accounts, so that this reference number is consistent across related records.',
      behavior: [
        'Trigger: Executed by AccountRegNoUpdate_Scheduler or manually',
        'Actions: Queries Accounts where Registration_Number_Updated__c = true; copies Registration_Number__c to Account_Registration_Number__c on all related Contacts, Opportunities, and Customer Accounts; resets flag; sends completion email with success/error counts',
        'Conditions: Batch size = 50',
      ],
      data: [
        'Fields: Registration_Number__c, Registration_Number_Updated__c (Account); Account_Registration_Number__c (Contact, Opportunity, IM_Customer_Account__c)',
        'Related Objects: Account, Contact, Opportunity, IM_Customer_Account__c',
      ],
    },
  },
  'AccountSegmentServices.ApexClass': {
    name: 'AccountSegmentServices',
    dependencyType: 'Service Class',
    overview: 'DEPRECATED - Segment/Sub-Segment auto-population',
    serviceNowConcept: 'N/A',
    serviceNowMapping: ['N/A - Do not migrate'],
    migrationConsiderations: ['Skip migration - code is commented out'],
    status: 'Deprecated',
    userStory: {
      description:
        '⚠️ DEPRECATED - Code is commented out\n\nAs a system, I need Account Segment/Sub-Segment picklist values automatically populated from lookup field selections.',
      behavior: ['Trigger: N/A (deprecated)', 'Actions: N/A', 'Conditions: N/A'],
      data: ['Fields: N/A', 'Related Objects: N/A'],
    },
  },
  'AccountTeamMemberDeleteClass.ApexClass': {
    name: 'AccountTeamMemberDeleteClass',
    dependencyType: 'Utility Class',
    overview: 'Bulk delete Account Team Members via custom button',
    serviceNowConcept: 'UI Action + Client Script',
    serviceNowMapping: ['List UI Action with GlideDialogWindow for selection'],
    migrationConsiderations: ['ServiceNow group membership may replace', 'Consider ACL approach'],
    status: 'Active',
    userStory: {
      description:
        'As an account manager, I need to bulk delete Account Team Members from an Account, so that I can efficiently manage team assignments.',
      behavior: [
        'Trigger: User clicks custom button on Account page',
        'Actions: Displays checkbox list of current Account Team Members (up to 10); deletes selected members on confirmation; redirects back to Account',
        'Conditions: Only users with profiles listed in AccountTeamMemberProfileSettings__c custom setting can access',
      ],
      data: [
        'Fields: AccountAccessLevel, UserId, TeamMemberRole',
        'Related Objects: Account, AccountTeamMember, AccountTeamMemberProfileSettings__c (Custom Setting)',
      ],
    },
  },
  'AccountTrigger_Test.ApexClass': {
    name: 'AccountTrigger_Test',
    dependencyType: 'Test Class',
    overview: 'Tests for Account trigger logic',
    serviceNowConcept: 'ATF Test Suite',
    serviceNowMapping: ['ATF tests covering Account Business Rules'],
    migrationConsiderations: ['Create comprehensive ATF suite for Account automations'],
    status: 'Test Class',
    userStory: {
      description: 'ℹ️ TEST CLASS - No functional migration needed',
      purpose:
        'Provides unit test coverage for Account trigger logic (AccountTriggerHandler and AccountHelper).',
      testCoverageFor: 'AccountTriggerHandler, AccountHelper',
      note: 'ServiceNow will need equivalent test coverage for whatever automation replaces the Account trigger functionality.',
    },
  },
  'AccountTriggerHandler.ApexClass': {
    name: 'AccountTriggerHandler',
    dependencyType: 'Trigger Handler',
    overview: 'Routes Account trigger events to helper methods',
    serviceNowConcept: 'Multiple Business Rules',
    serviceNowMapping: ['Individual BRs replace centralized handler pattern'],
    migrationConsiderations: ['ServiceNow uses separate BRs', 'No handler pattern needed'],
    status: 'Active',
    userStory: {
      description:
        'As a system, I need a centralized handler to route Account trigger events to the appropriate helper methods, so that trigger logic is organized and maintainable.',
      behavior: [
        'Trigger: Account trigger fires (before insert, before update, before delete, after insert, after update)',
        'Actions: Routes to AccountHelper and QTB_AccountHelper methods based on trigger context; implements ITriggerHandler interface',
        "Conditions: Can be disabled via Metadata_Activation_Settings__c custom setting (Disable_Trigger__c = true); checks SObject_API_Name__c for 'Account'",
      ],
      data: [
        'Fields: N/A (routing only)',
        'Related Objects: Account, Metadata_Activation_Settings__c (Custom Setting)',
      ],
    },
  },
  'ActivityService.ApexClass': {
    name: 'ActivityService',
    dependencyType: 'Service Class',
    overview: 'DISABLED - Rolls up Task/Event data to Opportunities',
    serviceNowConcept: 'Business Rule (if enabled)',
    serviceNowMapping: ['After Insert BR on Task/Activity to update Opportunity'],
    migrationConsiderations: ['Confirm if feature should be enabled', 'Implement as BR if needed'],
    status: 'Disabled',
    userStory: {
      description:
        '⚠️ Logic currently disabled (#16914)\n\nAs a system, I need Task/Event activity data rolled up to Opportunities, so that Customer Verifier checkboxes and RIM360 fields are auto-populated based on activity selections.',
      behavior: [
        'Trigger: Task or Event is created/updated with Customer_Verifiers__c or RIM360__c populated',
        'Actions: Parses multi-select picklist values and sets corresponding checkbox fields on the related Opportunity (Customer_Verifier_2__c through Customer_Verifier_16__c, RIM360_1__c)',
        'Conditions: Currently commented out - no active logic',
      ],
      data: [
        'Fields: Customer_Verifiers__c, RIM360__c (Activity); Customer_Verifier_X__c, RIM360_1__c (Opportunity); IM_Job_Level__c (Contact)',
        'Related Objects: Task, Event, Opportunity, Contact',
      ],
    },
  },
  'AddProductClass.ApexClass': {
    name: 'AddProductClass',
    dependencyType: 'Utility Class',
    overview: 'Complex wizard for adding products to Opportunities with bundles, GSA compliance',
    serviceNowConcept: 'Flow Designer + Catalog Item',
    serviceNowMapping: ['Record Producer or Flow with multiple stages'],
    migrationConsiderations: [
      'COMPLEX - Break into multiple flows',
      'Consider Catalog Item approach',
    ],
    status: 'Active (Complex)',
    userStory: {
      description:
        '⚠️ Complex - recommend breaking into smaller ServiceNow flows\n\nAs a sales rep, I need a guided wizard to add products to Opportunities, so that I can configure product bundles, answer product-specific questions, and ensure GSA compliance for government opportunities.',
      behavior: [
        'Trigger: User clicks "Add Product" button on Opportunity',
        'Actions:',
        'Validates Opportunity is not synced with a Quote',
        'Prompts for Contact Role assignment if none exists',
        'Offers product bundle selection or manual product class/line selection',
        'Displays product-specific questions based on Opportunity_Questions__c',
        'Captures pricing and quantity (supports list price override, quantity override, recurring revenue ×12)',
        'For GSA-eligible opportunities: displays GSA-specific questions and triggers approval workflow if any answer is "Yes"',
        'For Records Management Storage products: captures Vended/Unvended percentages',
        'Creates OpportunityLineItems and Opportunity_Answers__c records',
        'Conditions: Profile must match Opportunity_Product_Wizard_Settings__c; blocks if IMGS approval is "Under Review"; handles currency-specific pricebooks',
      ],
      data: [
        'Fields: Bundle__c, GSA_Eligibility__c, IMGS_Approval_Status__c, IME_Vended_Unvended__c, SyncedQuoteId, CurrencyIsoCode, Pricebook2Id',
        'Related Objects: Opportunity, OpportunityLineItem, OpportunityContactRole, PricebookEntry, Product2, Product_Bundle__c, Opportunity_Questions__c, Opportunity_Answers__c, Contact',
      ],
    },
  },
  'AgedTaskDeletion.ApexClass': {
    name: 'AgedTaskDeletion',
    dependencyType: 'Batch Class',
    overview: "Deletes old 'Not Started' tasks without due dates",
    serviceNowConcept: 'Scheduled Job',
    serviceNowMapping: ['Table Cleaner or Scheduled Script with GlideRecord delete'],
    migrationConsiderations: [
      'Use ServiceNow Table Cleaner if possible',
      'Otherwise scheduled script',
    ],
    status: 'Active',
    userStory: {
      description:
        "As a system administrator, I need old open tasks automatically deleted, so that the system doesn't accumulate stale task records.",
      behavior: [
        'Trigger: Executed by AgedTaskDeletionScheduleClass or manually',
        'Actions: Queries Tasks matching criteria; deletes them and empties recycle bin; sends email with CSV attachments listing successes and errors',
        "Conditions: Tasks where Status = 'Not Started' AND Due_Date__c is null AND CreatedDate is older than configured days (from SendTaskDeleteEmail__c.AgeingDays__c) AND IsRecurrence = false",
      ],
      data: [
        'Fields: Subject, Status, Due_Date__c, CreatedDate, IsRecurrence',
        'Related Objects: Task, SendTaskDeleteEmail__c (Custom Setting)',
      ],
    },
  },
  'AgedTaskDeletionScheduleClass.ApexClass': {
    name: 'AgedTaskDeletionScheduleClass',
    dependencyType: 'Schedulable',
    overview: 'Schedules aged task deletion batch',
    serviceNowConcept: 'Scheduled Job',
    serviceNowMapping: ['sys_trigger for cleanup job'],
    migrationConsiderations: ['Configure schedule', 'Consider Table Rotation policies'],
    status: 'Active',
    userStory: {
      description:
        'As a system administrator, I need the aged task deletion to run on a schedule, so that stale tasks are automatically cleaned up without manual intervention.',
      behavior: [
        'Trigger: Scheduled job execution (cron-based)',
        'Actions: Executes AgedTaskDeletion batch',
        'Conditions: None - simply kicks off the batch',
      ],
      data: ['Fields: N/A (scheduling only)', 'Related Objects: Task'],
    },
  },
  'AnnouncementModel.ApexClass': {
    name: 'AnnouncementModel',
    dependencyType: 'Model Class',
    overview: 'DTO for announcement content and URL',
    serviceNowConcept: 'Script Include (Object)',
    serviceNowMapping: ['JavaScript object in Script Include'],
    migrationConsiderations: ['Simple data structure', 'Embed in consuming Script Include'],
    status: 'Active',
    userStory: {
      description:
        'As a developer, I need a data transfer object to hold announcement information, so that announcement content and URLs can be passed between components.',
      behavior: [
        'Trigger: Instantiated when announcement data needs to be structured',
        'Actions: Stores content (text) and url properties',
        'Conditions: Simple POJO/DTO with constructor accepting content and url strings',
      ],
      data: [
        'Fields: content (String), url (String)',
        'Related Objects: None - utility model class',
      ],
    },
  },
  'ApexJobProxyDefault.ApexClass': {
    name: 'ApexJobProxyDefault',
    dependencyType: 'Proxy Class',
    overview: 'Proxy pattern for async job execution - enables testing',
    serviceNowConcept: 'Script Include',
    serviceNowMapping: ['Not needed - ServiceNow testing differs'],
    migrationConsiderations: ['Pattern not required', 'ServiceNow ATF handles async differently'],
    status: 'Active',
    userStory: {
      description:
        'As a developer, I need a proxy pattern for Apex job operations, so that batch, queueable, and scheduled jobs can be properly unit tested.',
      behavior: [
        'Trigger: Called by classes that need to execute async jobs',
        'Actions:',
        'executeBatch() - wraps Database.executeBatch()',
        'enqueueJob() - wraps System.enqueueJob()',
        'queryCronTrigger() - queries CronTrigger for scheduled job info',
        'Conditions: Implements IApexJobProxy interface; default production implementation',
      ],
      data: ['Fields: N/A (proxy pattern)', 'Related Objects: CronTrigger, AsyncApexJob'],
    },
  },
  'ApexJobProxyDefaultTest.ApexClass': {
    name: 'ApexJobProxyDefaultTest',
    dependencyType: 'Test Class',
    overview: 'Tests for ApexJobProxyDefault',
    serviceNowConcept: 'N/A',
    serviceNowMapping: ['N/A - Test infrastructure class'],
    migrationConsiderations: ['Skip - ServiceNow uses different test patterns'],
    status: 'Test Class',
    userStory: {
      description: 'ℹ️ TEST CLASS - No functional migration needed',
      purpose: 'Provides unit test coverage for ApexJobProxyDefault class.',
      testCoverageFor: 'ApexJobProxyDefault',
      note: 'Infrastructure test class - tests the proxy pattern implementation.',
    },
  },
  'ApexJobProxyMock.ApexClass': {
    name: 'ApexJobProxyMock',
    dependencyType: 'Mock Class',
    overview: 'Mock for testing async job execution',
    serviceNowConcept: 'N/A',
    serviceNowMapping: ['N/A - Test infrastructure class'],
    migrationConsiderations: ['Skip - ATF handles mocking differently'],
    status: 'Test Class',
    userStory: {
      description: 'ℹ️ TEST CLASS - No functional migration needed',
      purpose:
        'Mock implementation of IApexJobProxy for unit testing classes that schedule/enqueue jobs without actually executing them.',
      testCoverageFor: 'Used by test classes to mock async job execution',
      note: 'Tracks calls to executeBatch and enqueueJob for test assertions; generates fake AsyncApexJob IDs.',
    },
  },
  'App_Constants.ApexClass': {
    name: 'App_Constants',
    dependencyType: 'Constants Class',
    overview: 'Centralized constants - Record Type IDs, picklist values, etc.',
    serviceNowConcept: 'System Property + Script Include',
    serviceNowMapping: ['System Properties for IDs', 'Script Include for constants'],
    migrationConsiderations: [
      'Use sys_properties for configurable values',
      'Script Include for static',
    ],
    status: 'Active',
    userStory: {
      description:
        'As a developer, I need a centralized constants class, so that Record Type IDs, picklist values, and other reusable constants are defined once and consistently referenced.',
      behavior: [
        'Trigger: Referenced by various Apex classes and triggers',
        'Actions: Provides static final constants for:',
        'Opportunity Record Type IDs (IME, IMAP, IMLA, Standard, CPQ)',
        'Opportunity Stage values',
        'Contract Record Type IDs',
        'Contract Term Type picklist values',
        'Contract Status picklist values',
        'Contract RecordType DeveloperNames',
        'Conditions: Schema.SObjectType methods used for dynamic Record Type ID retrieval',
      ],
      data: [
        'Fields: Various Record Type IDs, Stage sets, Status values',
        'Related Objects: Opportunity, Contract, RecordType',
      ],
    },
  },
  'ArchivedEmailDetailController.ApexClass': {
    name: 'ArchivedEmailDetailController',
    dependencyType: 'Visualforce Controller',
    overview: 'Displays archived email detail from Big Object with attachments',
    serviceNowConcept: 'Widget + REST API',
    serviceNowMapping: ['Service Portal Widget querying archive table'],
    migrationConsiderations: ['Need equivalent archive table', 'Consider Email Archive plugin'],
    status: 'Active',
    userStory: {
      description:
        'As a customer service agent, I need to view archived email details including attachments, so that I can review historical case communications stored in big objects.',
      behavior: [
        'Trigger: User opens ArchivedEmailDetail Visualforce page with caseId and emailId parameters',
        'Actions:',
        'Queries Archive_Email__b big object for the specific email',
        'Retrieves related attachments (both Attachment and ContentDocument objects)',
        'Displays email fields: From, To, Cc, Bcc, Subject, Body, Status, Date',
        'Shows attachment list with name, size, and download links',
        'Conditions: Requires caseId and emailId URL parameters; parses ContentDocumentIDs__c for file attachments',
      ],
      data: [
        'Fields: BccAddress__c, CcAddress__c, FromAddress__c, FromName__c, Status__c, Subject__c, TextBody__c, ToAddress__c, MessageDate__c, ContentDocumentIDs__c',
        'Related Objects: Archive_Email__b, Attachment, ContentDocument, Case',
      ],
    },
  },
  'ArchivedEmailDetailControllerTest.ApexClass': {
    name: 'ArchivedEmailDetailControllerTest',
    dependencyType: 'Test Class',
    overview: 'Tests for archived email detail controller',
    serviceNowConcept: 'ATF Test',
    serviceNowMapping: ['ATF test for archive display widget'],
    migrationConsiderations: ['Create ATF tests for archive functionality'],
    status: 'Test Class',
    userStory: {
      description: 'ℹ️ TEST CLASS - No functional migration needed',
      purpose: 'Provides unit test coverage for ArchivedEmailDetailController.',
      testCoverageFor: 'ArchivedEmailDetailController',
      note: 'ServiceNow will need equivalent test coverage for archived email viewing functionality.',
    },
  },
  'ArchivedEmailListViewController.ApexClass': {
    name: 'ArchivedEmailListViewController',
    dependencyType: 'Visualforce Controller',
    overview: 'Paginated list of archived emails for a Case',
    serviceNowConcept: 'Related List + Widget',
    serviceNowMapping: ['Embedded List or Service Portal Widget'],
    migrationConsiderations: ['Could use standard Related List if archive table created'],
    status: 'Active',
    userStory: {
      description:
        'As a customer service agent, I need to see a paginated list of archived emails for a Case, so that I can browse historical communications without loading all records at once.',
      behavior: [
        'Trigger: User views ArchivedEmailListView page or embedded component on Case',
        'Actions:',
        'Queries Archive_Email__b big object for all emails related to Case',
        'Implements client-side pagination (configurable page size, default 5)',
        'Displays: Subject, Status, To Address, Message Date',
        'Previous/Next navigation with disable logic at boundaries',
        '"Full List" button when multiple pages exist',
        'Conditions: Accepts caseId and optional displaySize URL parameters',
      ],
      data: [
        'Fields: Case__c, Email__c, Status__c, Subject__c, MessageDate__c, ToAddress__c',
        'Related Objects: Archive_Email__b, Case',
      ],
    },
  },
  'ArchivedEmailListViewControllerTest.ApexClass': {
    name: 'ArchivedEmailListViewControllerTest',
    dependencyType: 'Test Class',
    overview: 'Tests for archived email list',
    serviceNowConcept: 'ATF Test',
    serviceNowMapping: ['ATF test for list widget'],
    migrationConsiderations: ['Create ATF tests'],
    status: 'Test Class',
    userStory: {
      description: 'ℹ️ TEST CLASS - No functional migration needed',
      purpose: 'Provides unit test coverage for ArchivedEmailListViewController.',
      testCoverageFor: 'ArchivedEmailListViewController',
      note: 'ServiceNow will need equivalent test coverage for archived email list functionality.',
    },
  },
  'ArchiveEmailBatchable.ApexClass': {
    name: 'ArchiveEmailBatchable',
    dependencyType: 'Batch Class',
    overview: 'Archives Case emails to Big Object for storage optimization',
    serviceNowConcept: 'Scheduled Job',
    serviceNowMapping: ['Scheduled Script moving emails to archive table'],
    migrationConsiderations: ['Create archive table', 'Scheduled job to move old emails'],
    status: 'Active',
    userStory: {
      description:
        'As a system administrator, I need Case email messages automatically archived to big objects, so that storage is optimized while maintaining access to historical communications.',
      behavior: [
        'Trigger: Executed by ArchiveEmailSchedulable or manually',
        'Actions:',
        'Calls service.buildQueryLocator() to get EmailMessages to archive',
        'Processes each batch via service.archiveEmails()',
        'Emits any accumulated errors on finish',
        'Conditions: Uses IArchiveEmailService interface for testability; stateful to track errors across batches',
      ],
      data: [
        'Fields: EmailMessage fields → Archive_Email__b fields',
        'Related Objects: EmailMessage, Archive_Email__b, Case',
      ],
    },
  },
  'ArchiveEmailBatchableTest.ApexClass': {
    name: 'ArchiveEmailBatchableTest',
    dependencyType: 'Test Class',
    overview: 'Tests for email archive batch',
    serviceNowConcept: 'ATF Test',
    serviceNowMapping: ['ATF test for archive scheduled job'],
    migrationConsiderations: ['Create ATF tests'],
    status: 'Test Class',
    userStory: {
      description: 'ℹ️ TEST CLASS - No functional migration needed',
      purpose: 'Provides unit test coverage for ArchiveEmailBatchable.',
      testCoverageFor: 'ArchiveEmailBatchable',
      note: 'ServiceNow will need equivalent test coverage for email archiving batch process.',
    },
  },
  'ArchiveEmailDataServiceDefault.ApexClass': {
    name: 'ArchiveEmailDataServiceDefault',
    dependencyType: 'Data Service Class',
    overview: 'Data access layer for email archiving - big object DML',
    serviceNowConcept: 'Script Include',
    serviceNowMapping: ['Script Include for archive table operations'],
    migrationConsiderations: ['Implement as Script Include with GlideRecord ops'],
    status: 'Active',
    userStory: {
      description:
        'As a system, I need a data access layer for email archive operations, so that big object inserts and deletes are encapsulated and testable.',
      behavior: [
        'Trigger: Called by ArchiveEmailService',
        'Actions:',
        'upsertBigObject() - Database.insertImmediate for Archive_Email__b records',
        'upsertContentDocLink() - Creates ContentDocumentLink records',
        'deleteBigObject() - @future method to delete archived emails when Cases are deleted',
        'Conditions: Implements IArchiveEmailDataService interface; uses immediate DML for big objects',
      ],
      data: [
        'Fields: Archive_Email__b fields, ContentDocumentLink fields',
        'Related Objects: Archive_Email__b, ContentDocumentLink, Case',
      ],
    },
  },
  'ArchiveEmailDataServiceDefaultTest.ApexClass': {
    name: 'ArchiveEmailDataServiceDefaultTest',
    dependencyType: 'Test Class',
    overview: 'Tests for archive data service',
    serviceNowConcept: 'ATF Test',
    serviceNowMapping: [
      'ATF test suite validating archive data service operations including create, read, query functions...',
    ],
    migrationConsiderations: ['Create ATF tests'],
    status: 'Test Class',
    userStory: {
      description: 'ℹ️ TEST CLASS - No functional migration needed',
      purpose: 'Provides unit test coverage for ArchiveEmailDataServiceDefault.',
      testCoverageFor: 'ArchiveEmailDataServiceDefault',
      note: 'ServiceNow will need equivalent test coverage for email archive data service.',
    },
  },
  'ArchiveEmailDataServiceMock.ApexClass': {
    name: 'ArchiveEmailDataServiceMock',
    dependencyType: 'Mock Class',
    overview: 'Mock for archive data service testing',
    serviceNowConcept: 'N/A',
    serviceNowMapping: ['N/A - Test mock class'],
    migrationConsiderations: ['Skip - ATF handles differently'],
    status: 'Test Class',
    userStory: {
      description: 'ℹ️ TEST CLASS - No functional migration needed',
      purpose:
        'Mock implementation of IArchiveEmailDataService for unit testing without actual big object DML.',
      testCoverageFor: 'Used by ArchiveEmailServiceTest to mock data layer',
      note: 'Captures upserted records and deleted case IDs for test assertions.',
    },
  },
  'ArchiveEmailScheduableTest.ApexClass': {
    name: 'ArchiveEmailScheduableTest',
    dependencyType: 'Test Class',
    overview: 'Tests for archive scheduler',
    serviceNowConcept: 'ATF Test',
    serviceNowMapping: [
      'ATF test validating scheduler configuration and execution timing for email archive job...',
    ],
    migrationConsiderations: ['Create ATF tests'],
    status: 'Test Class',
    userStory: {
      description: 'ℹ️ TEST CLASS - No functional migration needed',
      purpose: 'Provides unit test coverage for ArchiveEmailSchedulable.',
      testCoverageFor: 'ArchiveEmailSchedulable',
      note: 'ServiceNow will need equivalent test coverage for scheduled email archiving.',
    },
  },
  'ArchiveEmailSchedulable.ApexClass': {
    name: 'ArchiveEmailSchedulable',
    dependencyType: 'Schedulable',
    overview: 'Schedules email archiving batch',
    serviceNowConcept: 'Scheduled Job',
    serviceNowMapping: ['sys_trigger for archive job'],
    migrationConsiderations: ['Configure schedule frequency'],
    status: 'Active',
    userStory: {
      description:
        'As a system administrator, I need email archiving to run on a schedule, so that old Case emails are automatically archived without manual intervention.',
      behavior: [
        'Trigger: Scheduled job execution (cron-based)',
        'Actions: Executes ArchiveEmailBatchable with scope size from Archived_Email_Batch_Scope_Size custom label',
        'Conditions: Uses ApexJobProxyDefault for batch execution (supports mocking in tests)',
      ],
      data: ['Fields: N/A (scheduling only)', 'Related Objects: EmailMessage, Archive_Email__b'],
    },
  },
  'ArchiveEmailService.ApexClass': {
    name: 'ArchiveEmailService',
    dependencyType: 'Service Class',
    overview:
      'Business logic for email archiving - converts emails to big objects, migrates attachments',
    serviceNowConcept: 'Script Include',
    serviceNowMapping: ['Script Include with archive logic'],
    migrationConsiderations: [
      'COMPLEX - attachment handling needs careful migration',
      'Consider ServiceNow attachment APIs',
    ],
    status: 'Active',
    userStory: {
      description:
        'As a system, I need business logic for email archiving, so that EmailMessages are converted to big objects with attachments properly migrated.',
      behavior: [
        'Trigger: Called by ArchiveEmailBatchable',
        'Actions:',
        'buildQueryLocator() - Queries EmailMessages based on Close_Case_Interval__mdt settings (day/email intervals, date ranges, case status)',
        'archiveEmails() - Converts EmailMessage to Archive_Email__b, handles status mapping, migrates attachments to Case, creates ContentDocumentLinks, deletes original EmailMessages',
        'clearArchiveEmailOnCaseDelete() - Deletes archived emails when parent Case is deleted',
        'Comprehensive error logging via ILogger',
        'Conditions: Respects heap size limits when processing attachments; uses configurable intervals from custom metadata',
      ],
      data: [
        'Fields: BccAddress, CcAddress, FromAddress, Subject, TextBody, Status, MessageDate (EmailMessage) → corresponding __c fields (Archive_Email__b)',
        'Related Objects: EmailMessage, Archive_Email__b, Case, Attachment, ContentDocument, ContentDocumentLink, Close_Case_Interval__mdt',
      ],
    },
  },
  'ArchiveEmailServiceTest.ApexClass': {
    name: 'ArchiveEmailServiceTest',
    dependencyType: 'Test Class',
    overview: 'Tests for archive service',
    serviceNowConcept: 'ATF Test',
    serviceNowMapping: ['ATF test suite'],
    migrationConsiderations: ['Create comprehensive ATF tests'],
    status: 'Test Class',
    userStory: {
      description: 'ℹ️ TEST CLASS - No functional migration needed',
      purpose: 'Provides unit test coverage for ArchiveEmailService.',
      testCoverageFor: 'ArchiveEmailService',
      note: 'ServiceNow will need equivalent test coverage for email archiving service logic.',
    },
  },
  'AssignLeads.ApexClass': {
    name: 'AssignLeads',
    dependencyType: 'Utility Class',
    overview: 'Applies lead assignment rules when Reassign_Lead__c is checked',
    serviceNowConcept: 'Assignment Rule + Business Rule',
    serviceNowMapping: ['Assignment Rule on Lead + Before Update BR to reset flag'],
    migrationConsiderations: ['Use ServiceNow Assignment Rules', 'BR to trigger reassignment'],
    status: 'Active',
    userStory: {
      description:
        'As a sales operations manager, I need Leads reassigned using assignment rules when flagged, so that leads are routed to the correct owners based on current assignment criteria.',
      behavior: [
        'Trigger: Called by reassignLeads trigger when Reassign_Lead__c = true',
        'Actions:',
        'Queries Leads by ID',
        'Sets Reassign_Lead__c = false',
        'Applies default Lead assignment rules via DMLOptions',
        'Updates Leads to trigger reassignment',
        'Special handling for Stage 2 leads entered today (counter logic)',
        'Conditions: Uses assignAlreadyCalled flag to prevent recursion',
      ],
      data: [
        'Fields: Reassign_Lead__c, IM_Lead_Stage__c, IM_Stage_2_Entered_Date__c',
        'Related Objects: Lead, AssignmentRule',
      ],
    },
  },
};

export const flowServiceNowMapping: Record<string, ServiceNowEnrichment> = {
  'X14634_NAS_Escalations_Web_to_Case.Flow': {
    name: 'X14634_NAS_Escalations_Web_to_Case',
    dependencyType: 'Record-Triggered Flow (Before Save)',
    object: 'Case',
    overview:
      'Routes web-submitted Cases (Complaints, Escalations, Recognitions) to NAS_Escalations queue with proper categorization',
    serviceNowConcept: 'Business Rule',
    serviceNowMapping: ['Before Insert BR on Case checking Type field, setting Origin and Queue'],
    migrationConsiderations: ['Simple field mapping', 'Create equivalent queue in ServiceNow'],
    status: 'Active',
    userStory: {
      description:
        'As a customer service manager, I need web-submitted Cases automatically routed based on inquiry type, so that complaints, escalations, and recognitions reach the correct queue with proper categorization.',
      behavior: [
        "Trigger: Case is created with Type = 'Complaint', 'Escalation', or 'Recognition'",
        'Actions:',
        'Sets Origin based on Type (Web_Complaint, Web_Escalation, or Web_Recognition)',
        'Assigns RecordType = NAS_General_Support_Case',
        'Routes to NAS_Escalations queue',
        'Conditions: Only fires on Case create; Type must be Complaint, Escalation, or Recognition',
      ],
      data: [
        'Fields: Type, Origin, RecordTypeId, OwnerId',
        'Related Objects: Case, Group (Queue), RecordType',
      ],
    },
  },
  'X6Sense_Buying_Stage_Field_Update.Flow': {
    name: 'X6Sense_Buying_Stage_Field_Update',
    dependencyType: 'Record-Triggered Flow (Before Save)',
    object: 'Opportunity',
    overview: 'Copies 6Sense buying stage fields from Account to new Opportunities',
    serviceNowConcept: 'Business Rule',
    serviceNowMapping: ['Before Insert BR on Opportunity copying fields from parent Account'],
    migrationConsiderations: [
      'Need 6Sense integration in ServiceNow',
      'Straightforward field copy',
    ],
    status: 'Active',
    userStory: {
      description:
        "As a sales rep, I need 6Sense buying stage data automatically copied from Account to new Opportunities, so that I have immediate visibility into the prospect's buying intent.",
      behavior: [
        'Trigger: Opportunity is created',
        'Actions: Copies three 6Sense buying stage fields from related Account:',
        '6Sense_Buying_Stage_ALM__c',
        '6Sense_Buying_Stage_Digital__c',
        '6Sense_Buying_Stage_RM__c',
        'Conditions: Only fires on create; Account must have at least one 6Sense field populated',
      ],
      data: [
        'Fields: X6Sense_Buying_Stage_ALM__c, X6Sense_Buying_Stage_Digital__c, X6Sense_Buying_Stage_RM__c',
        'Related Objects: Opportunity, Account',
      ],
    },
  },
  'QTB_Operational_Account_Creation.Flow': {
    name: 'QTB_Operational_Account_Creation',
    dependencyType: 'Screen Flow',
    object: 'Account',
    overview:
      'Guided wizard for creating Operational Accounts with L1/L2/L3 hierarchy under Billing Accounts',
    serviceNowConcept: 'Flow Designer + Record Producer',
    serviceNowMapping: ['Multi-stage Flow Designer flow or Record Producer with UI Policies'],
    migrationConsiderations: [
      'COMPLEX - Recreate multi-step wizard',
      'Consider Catalog Item approach',
    ],
    status: 'Active',
    userStory: {
      description:
        'As a QTB billing administrator, I need a guided wizard to create Operational Accounts under Billing Accounts, so that I can set up the correct account hierarchy with proper pricing and address configuration.',
      behavior: [
        'Trigger: User launches flow from Billing Account',
        'Actions:',
        'Validates source is a Billing Account with QTB_FSO__c = true',
        'Collects Account Name, Level (L1/L2/L3), Operational Account Type',
        'For L2: requires Price List selection (Bundle or standard)',
        'For L3: requires L1 and L2 parent selection',
        'Validates Swedish postal codes (xxx xx format)',
        'For Bundle customers: updates Order Item taxable quantities',
        'Creates Operational Account with inherited fields (Currency, Country, Sector, Market)',
        'Conditions: Must start from Billing Account; supports L1, L2, L3 hierarchy; validates address by country',
      ],
      data: [
        'Fields: QTB_FSO__c, QTB_Bundle_Customer__c, QTB_Price_List__c, QTB_Customer_District__c, Related_Operational_Account__c, Related_Billing_Account__c',
        'Related Objects: Account, QTB_Price_List__c, RecordType',
      ],
    },
  },
  'account_closure_survey.Flow': {
    name: 'account_closure_survey',
    dependencyType: 'Survey Flow',
    object: '-',
    overview: 'Captures customer feedback when accounts close',
    serviceNowConcept: 'Survey + Flow',
    serviceNowMapping: ['Survey Designer survey triggered by Account status change'],
    migrationConsiderations: ['Use ServiceNow Survey plugin', 'Trigger via Business Rule'],
    status: 'Active (Survey)',
    userStory: {
      description:
        'As a customer success manager, I need to capture feedback when accounts close, so that we can understand reasons for churn and improve retention.',
      behavior: [
        'Trigger: Survey triggered on account closure event',
        'Actions: Collects customer feedback via survey questions',
        'Conditions: Associated with Account closure process',
      ],
      data: ['Fields: Survey response fields', 'Related Objects: Account, Survey responses'],
    },
  },
  'Account_for_Transition_to_False_after_6_months.Flow': {
    name: 'Account_for_Transition_to_False_after_6_months',
    dependencyType: 'Scheduled-Triggered Flow',
    object: 'Account',
    overview: 'Clears Account_for_Transition__c flag 6 months after Transition_date__c',
    serviceNowConcept: 'Scheduled Job',
    serviceNowMapping: ['Scheduled Script querying accounts past 6-month threshold'],
    migrationConsiderations: ['Simple date-based update', 'Scheduled script with GlideRecord'],
    status: 'Active',
    userStory: {
      description:
        'As a sales operations manager, I need Account transition flags automatically cleared after 6 months, so that accounts are no longer marked as "in transition" once the transition period expires.',
      behavior: [
        'Trigger: Scheduled path - 6 months after Transition_date__c',
        'Actions: Sets Account_for_Transition__c = false',
        'Conditions: Only processes Accounts where Account_for_Transition__c = true; uses Transition_date__c as time reference',
      ],
      data: ['Fields: Account_for_Transition__c, Transition_date__c', 'Related Objects: Account'],
    },
  },
  'Account_Lead_domain_match.Flow': {
    name: 'Account_Lead_domain_match',
    dependencyType: 'Record-Triggered Flow (After Save)',
    object: 'Lead',
    overview: 'Matches Leads to Accounts by email domain or company name',
    serviceNowConcept: 'Business Rule',
    serviceNowMapping: ['After Insert/Update BR with domain extraction and Account lookup'],
    migrationConsiderations: [
      'Implement domain parsing in Script Include',
      'Use GlideRecord for matching',
    ],
    status: 'Active',
    userStory: {
      description:
        'As a sales rep, I need Leads automatically matched to existing Accounts based on email domain, so that I can quickly identify if a lead is associated with a known customer.',
      behavior: [
        'Trigger: Lead is created or updated with Email populated',
        'Actions:',
        'Extracts domain from Lead email (after @)',
        "• Searches for Account with matching Account_Domain__c + BillingCountry + Division + IM_Hierarchy_Level = 'Country Parent'",
        'If no domain match, tries matching by Company name + Country + Hierarchy',
        'Updates Lead.Account__c lookup with matched Account',
        'Conditions: Lead must have Email and Company; Account__c must be null; Lead not already converted',
      ],
      data: [
        'Fields: Email, Company, Country, Division, Account__c (Lead); Account_Domain__c, BillingCountry, IM_Hierarchy_Level__c (Account)',
        'Related Objects: Lead, Account',
      ],
    },
  },
  'account_management_automated_survey.Flow': {
    name: 'account_management_automated_survey',
    dependencyType: 'Survey Flow',
    object: '-',
    overview: 'Sends automated surveys to account contacts',
    serviceNowConcept: 'Survey + Scheduled Flow',
    serviceNowMapping: ['Survey Designer with scheduled Flow trigger'],
    migrationConsiderations: ['Use Survey plugin with scheduled distribution'],
    status: 'Active (Survey)',
    userStory: {
      description:
        'As a customer success manager, I need automated surveys sent to account contacts, so that we can systematically collect customer feedback.',
      behavior: [
        'Trigger: Automated survey distribution',
        'Actions: Sends survey to account contacts',
        'Conditions: Based on Account Management survey criteria',
      ],
      data: [
        'Fields: Survey response fields',
        'Related Objects: Account, Contact, Survey responses',
      ],
    },
  },
  'account_management_manual_survey.Flow': {
    name: 'account_management_manual_survey',
    dependencyType: 'Survey Flow',
    object: '-',
    overview: 'Allows manual triggering of customer satisfaction surveys',
    serviceNowConcept: 'UI Action + Survey',
    serviceNowMapping: ['UI Action on Account triggering Survey distribution'],
    migrationConsiderations: ['UI Action calling Survey API'],
    status: 'Active (Survey)',
    userStory: {
      description:
        'As an account manager, I need to manually trigger customer satisfaction surveys, so that I can collect feedback at appropriate touchpoints in the customer relationship.',
      behavior: [
        'Trigger: Manual initiation by user',
        'Actions: Sends survey to selected contacts',
        'Conditions: User-initiated',
      ],
      data: [
        'Fields: Survey response fields',
        'Related Objects: Account, Contact, Survey responses',
      ],
    },
  },
  'account_management_survey.Flow': {
    name: 'account_management_survey',
    dependencyType: 'Survey Flow',
    object: '-',
    overview: 'Standard Account Management survey template',
    serviceNowConcept: 'Survey Template',
    serviceNowMapping: ['Survey Designer survey definition'],
    migrationConsiderations: ['Recreate survey questions in ServiceNow Survey Designer'],
    status: 'Active (Survey)',
    userStory: {
      description:
        'As a customer success team, I need a standard Account Management survey template, so that customer feedback is collected consistently.',
      behavior: [
        'Trigger: Survey distribution (manual or automated)',
        'Actions: Collects standardized customer feedback',
        'Conditions: Account Management survey context',
      ],
      data: ['Fields: Survey response fields', 'Related Objects: Account, Survey responses'],
    },
  },
  'Account_Transition_flow_to_store_former_values.Flow': {
    name: 'Account_Transition_flow_to_store_former_values',
    dependencyType: 'Record-Triggered Flow (Before Save)',
    object: 'Account',
    overview: 'Stores previous owner names in "Former" fields when coverage fields change',
    serviceNowConcept: 'Business Rule',
    serviceNowMapping: ['Before Update BR capturing previous() values into history fields'],
    migrationConsiderations: [
      'Use previous() function in BR',
      'Consider Audit History as alternative',
    ],
    status: 'Active',
    userStory: {
      description:
        'As a sales operations manager, I need former Account owner names preserved when coverage changes, so that we can track account transitions and maintain historical context.',
      behavior: [
        'Trigger: Account is updated with changes to any coverage field (AE/ISR, DBU SDE, ALM VSE, GMD, RIM SDE, AMD)',
        'Actions:',
        'Sets Account_for_Transition__c = true',
        'Captures Transition_date__c = current datetime',
        'For each changed field, stores previous owner\'s full name in corresponding "Former" field',
        'Excludes TBH (To Be Hired) placeholder users',
        'Conditions: Only captures former values if previous value was not blank and not "TBH"',
      ],
      data: [
        'Fields: Primary_BDE__c, Information_Governance_Rep__c, DMS_Owner__c, Global_IMGA_Owner__c, RIM_SDE__c, AMD__c (current); Former_AE_ISR_Account_Owner__c, Former_DBU_SDE__c, Former_ALM_VSE__c, Former_Global_Managing_Director__c, Former_RIM_SDE__c, Former_AMD__c (former)',
        'Related Objects: Account, User',
      ],
    },
  },
  'Account_Update_Territory_Country_with_D_B_Country.Flow': {
    name: 'Account_Update_Territory_Country_with_D_B_Country',
    dependencyType: 'Record-Triggered Flow (After Save)',
    object: 'Account',
    overview: 'Populates Territory Country from D&B data or billing address',
    serviceNowConcept: 'Business Rule',
    serviceNowMapping: ['After Insert/Update BR with country lookup logic'],
    migrationConsiderations: ['Need D&B integration', 'Implement country mapping table'],
    status: 'Active',
    userStory: {
      description:
        'As a territory administrator, I need Account Territory Country automatically populated from D&B data or billing address, so that territory assignments are accurate and consistent.',
      behavior: [
        'Trigger: Account is created or BillingCountry/D&B Company Profile changes',
        'Actions:',
        'Checks if DnB_Country__c has value - uses it for Territory Country',
        'If no D&B Country, uses BillingCountry',
        'Looks up matching IM_Country__c record (Active = true)',
        'Updates Territory_Country__c lookup',
        'If no match found, clears Territory_Country__c',
        'Conditions: Fires on create (if BillingCountry populated) or on change to BillingCountry or D&B profile',
      ],
      data: [
        'Fields: DnB_Country__c, BillingCountry, Territory_Country__c, DNBConnect__D_B_Connect_Company_Profile__c',
        'Related Objects: Account, IM_Country__c',
      ],
    },
  },
  'AccountTeamMemberAutomation.Flow': {
    name: 'AccountTeamMemberAutomation',
    dependencyType: 'Record-Triggered Flow (After Save)',
    object: 'Account',
    overview: 'Auto-creates/updates Account Team Members when coverage fields change (14 roles)',
    serviceNowConcept: 'Business Rule + Script Include',
    serviceNowMapping: ['After Update BR calling Script Include to manage group membership'],
    migrationConsiderations: [
      'COMPLEX - Map to ServiceNow groups or custom team table',
      '14 role mappings',
    ],
    status: 'Active',
    userStory: {
      description:
        'As a sales operations manager, I need Account Team Members automatically created/updated when Account coverage fields change, so that the account team always reflects current ownership assignments.',
      behavior: [
        'Trigger: Account is created or coverage field changes (14 fields monitored)',
        'Actions:',
        'For each coverage field change: finds existing ATM with matching role, deletes it, creates new ATM',
        'Creates ATM records for: GAM, GAR, MD, AE/ISR, AE/ISR Manager, Enterprise ISR, CSM, DBU SDE, ALM VSE/SE, ALM EAM, MAS Owner, LSO Owner, AMD, RIM SDE',
        'Sets appropriate access levels (Read/Edit) based on role',
        'Only creates if user is active',
        'Conditions: For Country Parent/Sales Site accounts, additional coverage fields are processed; skips inactive users',
      ],
      data: [
        'Fields: Global_Account_Manager__c, Global_Account_Director__c, Global_IMGA_Owner__c, Primary_BDE__c, Enterprise_ISR__c, Tele_CDE__c, Information_Governance_Rep__c, DMS_Owner__c, DM_Cloud_Sales_Rep__c, Primary_Local_Market_Rep_1__c, Library_Services_BDE__c, AMD__c, RIM_SDE__c',
        'Related Objects: Account, AccountTeamMember, User',
      ],
    },
  },
  'AccPlan_Approved_Post_to_Global_chatter_Groups.Flow': {
    name: 'AccPlan_Approved_Post_to_Global_chatter_Groups',
    dependencyType: 'Autolaunched Flow',
    object: 'Account_Plan__c',
    overview: 'Posts Chatter notification when Account Plan approved',
    serviceNowConcept: 'Business Rule + Connect Chat',
    serviceNowMapping: ['After Update BR posting to Connect Conversation'],
    migrationConsiderations: [
      'Use Connect Chat API or Notifications',
      'May need Connect workspace setup',
    ],
    status: 'Active',
    userStory: {
      description:
        'As a global account team, I need Chatter notifications when Account Plans are approved, so that stakeholders are informed of strategic planning milestones.',
      behavior: [
        'Trigger: Called when Account Plan is approved (invoked by another process)',
        'Actions:',
        'Looks up Chatter Group name from IMGAChatterGroup__c custom setting using Global Account Name',
        'Posts message: "Account Plan {planName} at {accountName}, {city} approved."',
        'Conditions: Only posts if matching Chatter Group exists in custom setting',
      ],
      data: [
        'Fields: Chatter_Group_Name__c (IMGAChatterGroup__c); accountPlanName, accountName, accountCity (input variables)',
        'Related Objects: IMGAChatterGroup__c, CollaborationGroup, FeedItem',
      ],
    },
  },
  'Active_Pipeline_Flag_and_AWS_Deal_Registration_Notification.Flow': {
    name: 'Active_Pipeline_Flag_and_AWS_Deal_Registration_Notification',
    dependencyType: 'Record-Triggered Flow (After Save)',
    object: 'Opportunity',
    overview: 'Sets Active Pipeline flag and sends AWS deal notifications based on stage/provider',
    serviceNowConcept: 'Business Rule + Notification',
    serviceNowMapping: ['After Insert/Update BR with flag logic + Email Notification'],
    migrationConsiderations: [
      'Split into field update BR and notification',
      'Configure email template',
    ],
    status: 'Active',
    userStory: {
      description:
        'As a sales operations manager, I need Opportunities automatically flagged as Active Pipeline and AWS deal registration notifications sent, so that pipeline reporting is accurate and AWS partnerships are properly tracked.',
      behavior: [
        'Trigger: Opportunity is created or updated',
        'Actions:',
        'Sets Active_Pipeline__c = true if:',
        "  - Active_Pipeline_Indicator_FORMULA__c = 'Current Year' or 'Next Year'",
        '  - AND Stage is 0-7 (any active stage)',
        'Sends AWS_Deal_Registration_Notification email if:',
        "  - Cloud_Provider__c = 'AWS'",
        "  - AND StageName = '2 - Solution Developed'",
        "  - AND Primary_Product_Line__c = 'Insight'",
        'Conditions: Migrated from workflow rules; evaluates on every create/update',
      ],
      data: [
        'Fields: Active_Pipeline__c, Active_Pipeline_Indicator_FORMULA__c, StageName, Cloud_Provider__c, Primary_Product_Line__c',
        'Related Objects: Opportunity, WorkflowAlert',
      ],
    },
  },
  'Actual_Total_Cost_On_Project_on_BE_created_reocrds.Flow': {
    name: 'Actual_Total_Cost_On_Project_on_BE_created_reocrds',
    dependencyType: 'Record-Triggered Flow (After Save)',
    object: 'Billing_Event__c',
    overview: 'Updates Project Actual Total Cost when Bill Code units change',
    serviceNowConcept: 'Business Rule',
    serviceNowMapping: ['After Update BR on Bill Code updating parent Project'],
    migrationConsiderations: ['PSA-specific', 'Need equivalent project/billing data model'],
    status: 'Active',
    userStory: {
      description:
        'As a project manager, I need Project Actual Total Cost automatically updated when Bill Code units change, so that I have accurate real-time cost tracking without manual recalculation.',
    },
  },
  'Actual_Total_Cost_Update_On_Project.Flow': {
    name: 'Actual_Total_Cost_Update_On_Project',
    dependencyType: 'Record-Triggered Flow (After Save)',
    object: 'Project__c',
    overview: 'Recalculates Project Actual Total Cost on demand via flag',
    serviceNowConcept: 'Business Rule',
    serviceNowMapping: ['After Update BR triggered by flag, summing child records'],
    migrationConsiderations: ['Use GlideAggregate for summation', 'Reset flag after calc'],
    status: 'Active',
    userStory: {
      description:
        'As a project manager, I need to recalculate Project Actual Total Cost on demand, so that I can ensure project financials are accurate after batch updates.',
      behavior: [
        'Trigger: Project Update_Actual_Total_Cost__c changes to true',
        'Actions:',
        'For Itemized: Sums Units_Complete_Cost__c from Bill Codes with Billing Event',
        'For Bundle: Sums from child (non-bundle) Bill Codes',
        'For Outcome: Sums from child Bill Codes under parent Bill Codes with Billing Events',
        'Updates Actual_Total_Cost__c and resets Update_Actual_Total_Cost__c = false',
        'Conditions: Only fires when Update_Actual_Total_Cost__c flips to true',
      ],
      data: [
        'Fields: Update_Actual_Total_Cost__c, Actual_Total_Cost__c, pse__Billing_Type__c (Project); Units_Complete_Cost__c, Billing_Event_ID__c, Bundle_Bill_Code__c, Parent_Bill_Code__c (Milestone)',
        'Related Objects: pse__Proj__c, pse__Milestone__c',
      ],
    },
  },
  'Actual_Total_Cost_Update_On_Project_For_Pre_Bill.Flow': {
    name: 'Actual_Total_Cost_Update_On_Project_For_Pre_Bill',
    dependencyType: 'Record-Triggered Flow (After Save)',
    object: 'Pre_Bill__c',
    overview: 'Updates Pre-Bill project costs from WIP records',
    serviceNowConcept: 'Business Rule',
    serviceNowMapping: ['After Insert/Update BR on WIP updating Project'],
    migrationConsiderations: ['PSA-specific', 'Need WIP table equivalent'],
    status: 'Active',
    userStory: {
      description:
        'As a project manager, I need Pre-Bill project costs updated from WIP records, so that project financials reflect non-billable work completion.',
      behavior: [
        'Trigger: WIP_Units_Complete__c record is created or WIP_Completed_Units__c changes',
        'Actions:',
        'For Pre-Bill projects: Sums Bill_Code_Completed_cost__c from all WIP records',
        'For non-Pre-Bill: Calculates difference and adds to Actual_Total_Cost__c',
        'Updates Project Actual_Total_Cost__c',
        'Conditions: Checks Project Pre_Bill__c flag to determine calculation method',
      ],
      data: [
        'Fields: WIP_Completed_Units__c, Bill_Code_Completed_cost__c, Project__c (WIP_Units_Complete__c); Actual_Total_Cost__c, Pre_Bill__c (Project)',
        'Related Objects: WIP_Units_Complete__c, pse__Proj__c',
      ],
    },
  },
  'Add_Chatter_Post_for_new_Opportunity_Team_Member_1.Flow': {
    name: 'Add_Chatter_Post_for_new_Opportunity_Team_Member_1',
    dependencyType: 'Record-Triggered Flow (After Save)',
    object: 'OpportunityTeamMember',
    overview: 'Notifies users when added to Opportunity Team',
    serviceNowConcept: 'Business Rule + Notification',
    serviceNowMapping: ['After Insert BR on OTM sending notification to user'],
    migrationConsiderations: ['Use ServiceNow notifications or Connect', 'Filter by profile'],
    status: 'Active',
    userStory: {
      description:
        "As an Opportunity team member, I need to be notified when added to an Opportunity, so that I'm aware of my involvement and can take appropriate action.",
      behavior: [
        'Trigger: OpportunityTeamMember is created',
        "Actions: Invokes 'Send Notification to new Opportunity Team Members' subflow with OTM record, Opportunity ID, and Record Type Name",
        "Conditions: User's Profile must NOT be in OpportunityTeamMember_NoChatter_Profiles custom label list",
      ],
      data: [
        'Fields: OpportunityId, TeamMemberRole, Opportunity.RecordType.Name',
        'Related Objects: OpportunityTeamMember, Opportunity, RecordType',
      ],
    },
  },
  'Add_Global_Managing_Director_as_Overlay_Split.Flow': {
    name: 'Add_Global_Managing_Director_as_Overlay_Split',
    dependencyType: 'Record-Triggered Flow (After Save)',
    object: 'Opportunity',
    overview: 'Auto-creates Overlay Splits for GMD on global account opportunities',
    serviceNowConcept: 'Business Rule + Script Include',
    serviceNowMapping: ['After Insert/Update BR checking hierarchy and creating split records'],
    migrationConsiderations: [
      'COMPLEX - Need split/compensation data model',
      'Hierarchy validation',
    ],
    status: 'Active',
    userStory: {
      description:
        'As a global account team, I need Overlay Splits automatically created for Global Managing Directors on global account opportunities, so that GMD compensation is properly tracked.',
      behavior: [
        'Trigger: Opportunity is created or Account changes',
        'Actions:',
        "• Validates IMGA account hierarchy (Global_Account = true, Program_Designation = 'Global Account', IM_Hierarchy_Level = 'IM Group (IMGA)')",
        'Creates Overlay Split with GMD as owner at 100%',
        "• Creates Opportunity Team Member with role = 'MD'",
        'If GMD already exists as OTM, checks and updates split to 100% if needed',
        'Conditions: Owner must not equal GMD; Opportunity must not be closed; specific Record Types only; Auto_create__c flags track flow-created records',
      ],
      data: [
        'Fields: Global_IMGA_Owner__c, Global_Account__c, Program_Designation__c, IM_Hierarchy_Level__c (Account); SplitOwnerId, SplitPercentage, Auto_create__c (OpportunitySplit); TeamMemberRole, Auto_Create__c (OpportunityTeamMember)',
        'Related Objects: Opportunity, Account, OpportunitySplit, OpportunityTeamMember',
      ],
    },
  },
  'Agent_Estimated_Wait_Time_Queue.Flow': {
    name: 'Agent_Estimated_Wait_Time_Queue',
    dependencyType: 'Routing Flow',
    object: 'AgentWork',
    overview: 'Calculates agent availability and wait times for chat routing across 20+ locales',
    serviceNowConcept: 'Advanced Work Assignment',
    serviceNowMapping: ['AWA rules with queue-based routing and availability checks'],
    migrationConsiderations: ['Use ITSM/CSM AWA', 'Configure queues per locale'],
    status: 'Active',
    userStory: {
      description:
        'As a customer service system, I need to calculate agent availability and estimated wait times for chat routing, so that customers receive accurate expectations and efficient service.',
      behavior: [
        'Trigger: Messaging Session routing evaluation',
        'Actions:',
        'Gets Messaging Session language and customer type (Guest/Authenticated)',
        'Determines appropriate queue based on language (supports 20+ locales: US, Canada French, UK, Spain, France, Germany, ANZ, India, Singapore, Malaysia, Philippines, Thailand, Taiwan, Indonesia, Hong Kong, LATAM countries, Korea)',
        'Checks agent availability in queue',
        'Returns isAgentAvailable flag and estimated wait time',
        'Conditions: Queue selection uses complex CASE formula based on EndUserLanguage and customerType',
      ],
      data: [
        'Fields: EndUserLanguage, Customer_type__c, Language_Selector__c (MessagingSession); DeveloperName (Group/Queue)',
        'Related Objects: MessagingSession, Group (Queue), ServiceChannel',
      ],
    },
  },
  'ANZ_MessagingSession_InboundFlow.Flow': {
    name: 'ANZ_MessagingSession_InboundFlow',
    dependencyType: 'Routing Flow',
    object: 'MessagingSession',
    overview:
      'Routes ANZ messaging sessions - creates contacts, checks agent availability, routes to queue or bot',
    serviceNowConcept: 'Advanced Work Assignment + Flow',
    serviceNowMapping: ['AWA with Flow for contact creation and bot fallback'],
    migrationConsiderations: [
      'COMPLEX - Need Virtual Agent integration for bot fallback',
      'AWA for routing',
    ],
    status: 'Active',
    userStory: {
      description:
        'As a customer service manager in ANZ, I need inbound messaging sessions automatically routed based on agent availability, so that customers connect with available agents or are handled by the bot when none are available.',
    },
  },
  'Assign_Agent_Phone_Voice_Essentials.Flow': {
    name: 'Assign_Agent_Phone_Voice_Essentials',
    dependencyType: 'Record-Triggered Flow (Before Save)',
    object: 'User',
    overview: 'Copies User Phone to Agent Phone field (Voice Essentials package)',
    serviceNowConcept: 'Business Rule',
    serviceNowMapping: ['Before Update BR on User copying phone field'],
    migrationConsiderations: ['Simple field sync', 'Check if CTI integration needed'],
    status: 'Active',
    userStory: {
      description:
        'As a Voice Essentials administrator, I need User Phone automatically copied to the Agent Phone field, so that agents have consistent phone configuration without manual entry.',
    },
  },
  'Assign_default_value_90_to_Alert_Threshold_on_project.Flow': {
    name: 'Assign_default_value_90_to_Alert_Threshold_on_project',
    dependencyType: 'Record-Triggered Flow (Before Save)',
    object: 'Project__c',
    overview: 'Sets default Alert Threshold on Projects and validates Service State',
    serviceNowConcept: 'Business Rule + Default Value',
    serviceNowMapping: ['Default Value on field + Before Insert BR for validation'],
    migrationConsiderations: ['Use dictionary default value', 'Add validation BR'],
    status: 'Active',
    userStory: {
      description:
        'As a project administrator, I need new Projects to default Alert Threshold to 90 and have Service State validated, so that projects are properly configured from creation.',
    },
  },
  'Assign_Split_Rep_2_to_Opportunity.Flow': {
    name: 'Assign_Split_Rep_2_to_Opportunity',
    dependencyType: 'Autolaunched Flow',
    object: 'Opportunity',
    overview: 'Populates Split Commission Rep 2 from OpportunitySplit record',
    serviceNowConcept: 'Business Rule',
    serviceNowMapping: ['After Insert BR on Split updating Opportunity'],
    migrationConsiderations: ['Need commission/split data model equivalent'],
    status: 'Active',
    userStory: {
      description:
        'As a sales operations manager, I need Split Commission Rep 2 automatically populated on Opportunities from OpportunitySplit records, so that commission splits are accurately tracked.',
    },
  },
  'Assign_Split_Reps_to_Opportunities.Flow': {
    name: 'Assign_Split_Reps_to_Opportunities',
    dependencyType: 'Autolaunched Flow',
    object: 'Opportunity',
    overview: 'Assigns Split Reps 1 and 2 to Opportunities from Split records',
    serviceNowConcept: 'Business Rule',
    serviceNowMapping: ['After Insert BR calling script to populate rep fields'],
    migrationConsiderations: ['Need split data model', 'Consider denormalization approach'],
    status: 'Active',
    userStory: {
      description:
        'As a sales operations manager, I need Split Reps 1 and 2 automatically assigned to Opportunities from Split records, so that commission assignments are maintained without manual updates.',
    },
  },
  'At_Risk_Red_Alert_NA.Flow': {
    name: 'At_Risk_Red_Alert_NA',
    dependencyType: 'Screen Flow',
    object: 'At_Risk__c',
    overview: 'NA region At Risk/Red Alert creation wizard - captures product line and reason',
    serviceNowConcept: 'Record Producer',
    serviceNowMapping: ['Record Producer with required fields for At Risk record'],
    migrationConsiderations: ['Create At Risk table', 'Configure Record Producer with validations'],
    status: 'Active',
    userStory: {
      description:
        'As an NA account manager, I need a guided wizard to create At Risk/Red Alert records that captures product line and reason, so that at-risk accounts are properly documented for review.',
    },
  },
  'At_Risk_Red_Alert_UK.Flow': {
    name: 'At_Risk_Red_Alert_UK',
    dependencyType: 'Screen Flow',
    object: 'At_Risk__c',
    overview: 'UK region At Risk wizard - validates no existing At Risk before creation',
    serviceNowConcept: 'Record Producer + Client Script',
    serviceNowMapping: ['Record Producer with async validation for existing records'],
    migrationConsiderations: ['Add client-side validation script', 'Configure UK-specific fields'],
    status: 'Active',
    userStory: {
      description:
        'As a UK account manager, I need an At Risk wizard that validates no existing At Risk exists before allowing creation, so that duplicate at-risk records are prevented.',
    },
  },
  'At_Risk_Red_Alert_UK_CustomerAccount.Flow': {
    name: 'At_Risk_Red_Alert_UK_CustomerAccount',
    dependencyType: 'Screen Flow',
    object: 'At_Risk__c',
    overview: 'UK At Risk for Customer Accounts - creates from Customer Account context',
    serviceNowConcept: 'Record Producer',
    serviceNowMapping: ['Record Producer accessible from Customer Account'],
    migrationConsiderations: ['Configure context-aware Record Producer'],
    status: 'Active',
    userStory: {
      description:
        'As a UK customer account manager, I need to create At Risk records directly from Customer Account context, so that I can quickly flag at-risk situations without navigating away.',
    },
  },
  'At_Risk_Red_Alert_UK_Outbound_Email_Notification.Flow': {
    name: 'At_Risk_Red_Alert_UK_Outbound_Email_Notification',
    dependencyType: 'Autolaunched Flow',
    object: 'At_Risk__c',
    overview: 'Sends email notifications for UK At Risk records using metadata-driven addresses',
    serviceNowConcept: 'Business Rule + Notification',
    serviceNowMapping: ['After Insert/Update BR triggering email notification'],
    migrationConsiderations: ['Configure email template', 'Use property for addresses by country'],
    status: 'Active',
    userStory: {
      description:
        'As a UK operations manager, I need automatic email notifications sent for At Risk records using metadata-driven addresses, so that stakeholders are informed without manual outreach.',
    },
  },
  'AtRisk_RedAlertChatterPost.Flow': {
    name: 'AtRisk_RedAlertChatterPost',
    dependencyType: 'Autolaunched Flow',
    object: 'At_Risk__c',
    overview: 'Posts Chatter alerts based on revenue thresholds (42K/100K/500K) and status changes',
    serviceNowConcept: 'Business Rule + Connect Chat',
    serviceNowMapping: ['After Update BR with threshold logic posting to Connect'],
    migrationConsiderations: ['COMPLEX - Revenue threshold logic', '@mentions for stakeholders'],
    status: 'Active',
    userStory: {
      description:
        'As a revenue operations manager, I need Chatter alerts automatically posted when revenue thresholds (42K/100K/500K) are crossed or status changes occur, so that leadership has visibility into significant account changes.',
    },
  },
  'Auto_Create_EISR_OTM.Flow': {
    name: 'Auto_Create_EISR_OTM',
    dependencyType: 'Record-Triggered Flow (After Save)',
    object: 'Opportunity',
    overview:
      'Auto-creates Enterprise ISR as Opportunity Team Member when Opp created or Account changes',
    serviceNowConcept: 'Business Rule',
    serviceNowMapping: ['After Insert/Update BR creating team member record'],
    migrationConsiderations: ['Need OTM equivalent table', 'Handle account change scenario'],
    status: 'Active',
    userStory: {
      description:
        'As a sales operations manager, I need Enterprise ISR automatically added as Opportunity Team Member when Opportunities are created or Account changes, so that ISRs have proper visibility into their accounts.',
    },
  },
  'Auto_Follow_Up_Email_For_Case.Flow': {
    name: 'Auto_Follow_Up_Email_For_Case',
    dependencyType: 'Record-Triggered Flow (After Save)',
    object: 'Case',
    overview:
      "Sends follow-up emails at 24/48/72 hours if customer hasn't replied to 'More Info' requests",
    serviceNowConcept: 'Scheduled Flow + Notifications',
    serviceNowMapping: ['Scheduled Flow checking reply status and sending emails'],
    migrationConsiderations: [
      'COMPLEX - Need business hour calculations',
      'Email templates per region',
    ],
    status: 'Active',
    userStory: {
      description:
        "As a customer service manager, I need automatic follow-up emails sent at 24/48/72 hours when customers haven't replied to 'More Info' requests, so that cases don't stall waiting for customer response.",
    },
  },
};

export const permissionSetServiceNowMapping: Record<string, ServiceNowEnrichment> = {
  'Access_to_Has_Billing_Account_and_No_of_Child_Account.PermissionSet': {
    name: 'Access_to_Has_Billing_Account_and_No_of_Child_Account',
    overview:
      'View billing account flag (read-only) and edit child account count on Account records. Created for ticket #26728 - likely supports billing/hierarchy reporting.',
    serviceNowConcept:
      'Role with field-level read/write permissions on account/company record. Simple additive permission for specific billing fields.',
    migrationConsiderations: [
      'Low complexity. Two fields only. Determine if these fields exist or need creation in target system. Identify which user personas need this access.',
    ],
  },
  'KC_AccessToKimbleCustomFields.PermissionSet': {
    name: 'KC_AccessToKimbleCustomFields',
    overview:
      'Access to custom fields on Kimble PSA Engagement records, including Google Drive folder link fields. Supports professional services project tracking integration.',
    serviceNowConcept:
      'Role for PSA/Project users needing GDrive integration visibility. May map to Project Management module access with custom field visibility.',
    migrationConsiderations: [
      'Medium complexity. Depends on PSA module strategy. Kimble is third-party PSA - evaluate if using native ServiceNow PSM or custom solution. GDrive links need integration consideration.',
    ],
  },
  'Account_Country_Assignment.PermissionSet': {
    name: 'Account_Country_Assignment',
    overview:
      'Edit access to Territory Country field on Account. Allows users to assign/change which country territory an account belongs to. Created per ticket #28225.',
    serviceNowConcept:
      'Role granting write access to territory/country assignment field on account record. Territory management permission.',
    migrationConsiderations: [
      'Low complexity. Single field. Need to map territory model to target system. Determine if territory assignment should have approval workflow.',
    ],
  },
  'Account_Coverage_Updates.PermissionSet': {
    name: 'Account_Coverage_Updates',
    overview:
      'Edit access to 23 sales coverage fields including: sales reps (AMD, BDE, ISR, GAM, etc.), territory, segment, sub-segment, strategic flags. Annual assignment requiring approval before granted.',
    serviceNowConcept:
      "Elevated role for sales operations/admin to manage account ownership and territory assignments. Should require approval workflow to grant. Covers the 'who owns this account' data model.",
    migrationConsiderations: [
      'High complexity. 23 fields spanning ownership, territory, and segmentation. Core to sales coverage model. Map user lookup fields carefully. Approval process for role assignment needed. May need custom coverage management module.',
    ],
  },
  'Account_Hierarchy_Level_Edit.PermissionSet': {
    name: 'Account_Hierarchy_Level_Edit',
    overview:
      'Full create/read/update/delete plus view-all and modify-all on Account and custom Customer Account object. Edit parent account, hierarchy level, segment, billing address, Oracle ID. Power-user access for account hierarchy management.',
    serviceNowConcept:
      'Admin-level role for account data stewards. Full table access to account and related customer account records. Likely maps to data governance or master data management function.',
    migrationConsiderations: [
      'High complexity. Elevated permissions (modify-all). Custom object IM_Customer_Account needs table creation. Parent-child hierarchy model. Oracle ID suggests ERP integration touchpoint. Restrict to data admin personas only.',
    ],
  },
  'Account_Team_Add_Edit_Access.PermissionSet': {
    name: 'Account_Team_Add_Edit_Access',
    overview:
      'Read and edit Account records plus ability to manage account team members (add/remove via custom page). Supports collaborative account ownership model.',
    serviceNowConcept:
      'Role allowing account record editing plus ability to manage team membership on accounts. May use group membership model or custom account team table.',
    migrationConsiderations: [
      'Medium complexity. Salesforce Account Teams concept may not have direct equivalent. Evaluate: group-based ownership vs. custom team junction table. Custom UI component for team management.',
    ],
  },
  'ChatAndAlertsObjectAccess.PermissionSet': {
    name: 'ChatAndAlertsObjectAccess',
    overview:
      'Integration user permission set for Salesforce Anywhere (Quip collaboration). Read + view-all on 218 objects across the org. NOT for end users - system integration account only.',
    serviceNowConcept:
      'Integration/service account role with broad read access. Web service or API user pattern. Should be assigned to integration user accounts, not humans.',
    migrationConsiderations: [
      'Integration pattern - not a user permission migration. Document which objects are accessed. Map to API/integration user setup in target. Consider OAuth scopes or service account permissions.',
    ],
  },
  'ALM_Access_except_Releases.PermissionSet': {
    name: 'ALM_Access_except_Releases',
    overview:
      'Application lifecycle management access without release/deployment capabilities. Cloned from ALM Admin with Release object removed. For developers who can view/edit but not deploy.',
    serviceNowConcept:
      'Developer role without deployment rights. Access to development/configuration tools but excluded from release management. Separation of dev vs. deploy duties.',
    migrationConsiderations: [
      'Medium complexity. Maps to DevOps/SDLC tooling access. Evaluate target ALM strategy (native tools, DevOps module, external). Key principle: separate build from deploy permissions.',
    ],
  },
  'ALM_BU_Forecast.PermissionSet': {
    name: 'ALM_BU_Forecast',
    overview:
      'Edit access to single field: ALM_BU_Forecast on Sales Revenue Forecast records. Allows business unit leaders to input their forecast numbers.',
    serviceNowConcept:
      'Role granting write access to BU forecast field on forecast/planning records. Narrow permission for forecast contributors.',
    migrationConsiderations: [
      'Low complexity. Single field permission. Custom object needs table creation. Consider: is this part of larger forecasting module? May integrate with planning/analytics tools.',
    ],
  },
  'ALM_BU_Forecast_Readonly.PermissionSet': {
    name: 'ALM_BU_Forecast_Readonly',
    overview:
      'Read-only access to ALM_BU_Forecast field on Sales Revenue Forecast records. View forecast data without edit capability. Paired with Editing version for complete access model.',
    serviceNowConcept:
      "Read-only role for forecast field visibility. Viewers/consumers of forecast data who shouldn't modify it.",
    migrationConsiderations: [
      'Low complexity. Consider consolidating with Editing version as single role with read/write tiers, or keep separate for cleaner audit trail.',
    ],
  },
};

export const profileServiceNowMapping: Record<string, ServiceNowEnrichment> = {
  'Admin.Profile': {
    name: 'Admin',
    overview:
      "Standard Salesforce System Administrator profile. Full platform access, all objects, all admin functions. Typically cloned/customized rather than used directly. Note: Listed as 'Admin' but may be the standard System Administrator profile.",
    serviceNowConcept:
      'admin or platform_admin role. Full system access. Should be highly restricted to true platform administrators only.',
    migrationConsiderations: [
      'Map users carefully - often over-assigned in Salesforce. Audit who truly needs admin vs. who has it for convenience. Consider breaking into specialized admin roles.',
    ],
  },
  'Analytics Cloud Integration User.Profile': {
    name: 'Analytics Cloud Integration User',
    overview:
      'Integration profile for Tableau CRM (Einstein Analytics) data sync. Allows analytics platform to read Salesforce data. System user, not for humans.',
    serviceNowConcept:
      'Integration user for analytics/BI platform connectivity. Service account with read access to data warehouse or reporting tables.',
    migrationConsiderations: [
      'Integration pattern. Map to analytics tool integration in target (Performance Analytics, external BI). Service account setup, not user migration.',
    ],
  },
  'Analytics Cloud Security User.Profile': {
    name: 'Analytics Cloud Security User',
    overview:
      'Security administration profile for Tableau CRM. Manages analytics sharing, dataset permissions, dashboard access. Analytics platform admin.',
    serviceNowConcept:
      'Analytics admin role. Manages report/dashboard sharing and data access within analytics tooling.',
    migrationConsiderations: [
      'Medium complexity. Map to analytics module admin if using native tools, or external BI platform admin role.',
    ],
  },
  'Anypoint Integration.Profile': {
    name: 'Anypoint Integration',
    overview:
      'Integration profile for MuleSoft Anypoint platform connectivity. API-level access for middleware/integration layer. System integration user.',
    serviceNowConcept:
      'Integration user/service account for middleware connectivity. IntegrationHub or REST API access. OAuth client credentials pattern.',
    migrationConsiderations: [
      'Integration architecture decision. MuleSoft → IntegrationHub or other middleware. Map API access patterns, not user permissions.',
    ],
  },
  'AS CSA.Profile': {
    name: 'AS CSA',
    overview:
      'Customer Service Agent profile for AS (likely Asset Services or similar business unit). Standard Salesforce license. Frontline service rep access pattern.',
    serviceNowConcept:
      'ITSM/CSM agent role for service desk users. Case/incident management access. Fulfiller role in service management.',
    migrationConsiderations: [
      "Core user persona. Map to CSM agent or ITSM fulfiller role. Understand what 'AS' business unit means for scoping. High user count likely.",
    ],
  },
  'AS NCSP Coordinator.Profile': {
    name: 'AS NCSP Coordinator',
    overview:
      'Coordinator role for AS NCSP program. Elevated above basic CSA - likely manages workload, assignments, escalations. Supervisory/coordination function.',
    serviceNowConcept:
      'Team lead or queue manager role. Assignment group manager permissions. May need workforce management or dispatch capabilities.',
    migrationConsiderations: [
      "Supervisor persona. Needs visibility across team's work. Assignment and escalation permissions. Consider ATF (Advanced Work Assignment) if using CSM.",
    ],
  },
  'AS NCSP CSA.Profile': {
    name: 'AS NCSP CSA',
    overview:
      'Customer Service Agent for NCSP sub-program within AS. Variant of AS CSA with program-specific access. Frontline agent for specific service line.',
    serviceNowConcept:
      'CSM/ITSM agent role scoped to specific assignment group or service offering. May have restricted case types or queues.',
    migrationConsiderations: [
      'Determine difference from base AS CSA. Likely queue/assignment group restriction rather than different permissions. Consider role inheritance.',
    ],
  },
  'AS NCSP Management.Profile': {
    name: 'AS NCSP Management',
    overview:
      'Management profile for AS NCSP program. Above Coordinator level - likely director/manager access with reporting, configuration, and oversight capabilities.',
    serviceNowConcept:
      'Service owner or program manager role. Dashboard/reporting access, configuration rights for assignment rules, SLA visibility.',
    migrationConsiderations: [
      'Manager persona. Needs reporting, team visibility, possibly light config rights. Map to manager role with appropriate report access.',
    ],
  },
  'B2B Reordering Portal Buyer Profile.Profile': {
    name: 'B2B Reordering Portal Buyer Profile',
    overview:
      'External portal user profile for B2B customers placing reorders. Community/Experience Cloud license. Limited access - can view own account, place orders, see order history.',
    serviceNowConcept:
      'Customer portal user role. Service Portal access with case submission and self-service capabilities. External user with scoped access to own data.',
    migrationConsiderations: [
      'External user persona. Portal/self-service pattern. Need to evaluate: Customer Portal, Service Portal, or B2B Commerce equivalent. Scoped to own account data only.',
    ],
  },
  'Chatter External User.Profile': {
    name: 'Chatter External User',
    overview:
      'External collaboration user - can participate in Chatter groups/conversations but no access to Salesforce data or objects. Partners or external stakeholders for communication only.',
    serviceNowConcept:
      'External collaborator with messaging/chat access only. May map to Virtual Agent chat, or external user with no table access. Consider: is this needed in target?',
    migrationConsiderations: [
      "Low priority if Chatter isn't core to operations. Evaluate if collaboration moves to Teams/Slack/other. If needed, map to minimal portal access for communication.",
    ],
  },
};

export const apexTriggerServiceNowMapping: Record<string, ServiceNowEnrichment> = {
  'AccountPlanImage.ApexTrigger': {
    name: 'AccountPlanImage',
    object: 'SFDC_Acct_Plan__c',
    overview:
      'Before insert/update trigger that extracts image URLs from rich text Organizational Chart field. Parses HTML to find <img> tags, extracts src URL, cleans amp; encoding, and stores in conga_text__c for Conga document generation. Also strips images from rich text for Word export.',
    serviceNowConcept:
      'Business Rule (before insert/update) with script to parse HTML content. May also need UI Policy or Client Script if real-time preview needed. Consider using HTML Sanitizer utilities.',
    migrationConsiderations: [
      'Medium complexity. HTML parsing logic needs rewrite. Conga integration suggests document generation - evaluate ServiceNow Document Templates or third-party doc gen. Rich text field handling differs between platforms.',
    ],
  },
  'AccountPlanObject.ApexTrigger': {
    name: 'AccountPlanObject',
    object: 'SFDC_Acct_Plan__c',
    overview:
      'Before insert/update trigger handling: (1) Owner lookup field sync with OwnerId, (2) Country Parent field population from Account hierarchy, (3) Global Account Plan validation - ensures only one Global Account Plan per IMGA account, prevents non-IMGA accounts from having Global plans. Uses custom settings for trigger activation control.',
    serviceNowConcept:
      'Business Rule with multiple functions: (1) Reference field auto-population, (2) Parent record field inheritance, (3) Validation with duplicate checking. Consider splitting into separate Business Rules for maintainability.',
    migrationConsiderations: [
      'High complexity. Multiple business functions in one trigger. IMGA hierarchy validation is core business logic. Custom settings → System Properties for feature flags. Recommend decomposing into separate rules in target.',
    ],
  },
  'AccountTrigger.ApexTrigger': {
    name: 'AccountTrigger',
    object: 'Account',
    overview:
      'Dispatcher pattern trigger on Account - fires on all events (before/after insert/update/delete/undelete). Delegates to AccountTriggerHandler class. Framework approach for centralized Account automation.',
    serviceNowConcept:
      "Business Rules (multiple) on core_company/account table. The dispatcher pattern isn't needed in ServiceNow - just create individual Business Rules for each use case. Handler class logic needs analysis.",
    migrationConsiderations: [
      'High complexity - this is a shell that calls handler class. Need to analyze AccountTriggerHandler to understand actual business logic. Likely contains many sub-functions that each become separate Business Rules.',
    ],
  },
  'ActivityDateUpdateEvent.ApexTrigger': {
    name: 'ActivityDateUpdateEvent',
    object: 'Event',
    overview:
      "After insert trigger on Event. Updates 'Most Recent Sales Activity' date on Contact/Lead when new Event is created. Excludes Eloqua integration users (from label). Calls MostRecentsaleActvt class.",
    serviceNowConcept:
      'Business Rule (after insert) on calendar/event table. Updates related contact record with last activity timestamp. Condition to exclude integration users by checking current.sys_created_by.',
    migrationConsiderations: [
      'Low complexity. Simple activity tracking pattern. Map Event → task or calendar_event table. Contact activity date field needed. Eloqua exclusion → integration user exclusion condition.',
    ],
  },
  'ActivityDateUpdateTask.ApexTrigger': {
    name: 'ActivityDateUpdateTask',
    object: 'Task',
    overview:
      "After insert trigger on Task. Same pattern as Event trigger - updates 'Most Recent Sales Activity' date on Contact/Lead when new Task is created. Excludes Eloqua users. Paired with ActivityDateUpdateEvent for complete activity tracking.",
    serviceNowConcept:
      'Business Rule (after insert) on task table. Same logic as Event rule - could potentially combine into single rule triggered by both tables, or keep separate for clarity.',
    migrationConsiderations: [
      'Low complexity. Pair with ActivityDateUpdateEvent migration. Same target field updated. Consider: single rule vs. two rules debate.',
    ],
  },
  'AttachmentObject.ApexTrigger': {
    name: 'AttachmentObject',
    object: 'Attachment',
    overview:
      "After insert trigger on Attachment. Creates notification Task for Case owner when attachment added to their Case (if owner isn't the one adding it). Prevents attachment on closed Cases. Uses custom settings for trigger activation. Bypasses during email archive batch jobs.",
    serviceNowConcept:
      'Business Rule on sys_attachment table. Creates task/notification when attachment added to case. Condition: case not closed, attacher != owner. May use Event + Notification instead of creating Task record.',
    migrationConsiderations: [
      'Medium complexity. Attachment handling differs - ServiceNow uses sys_attachment. Notification pattern: consider using actual Notifications rather than Task creation. Batch bypass → Business Rule condition on running_as_system.',
    ],
  },
  'BillCodeTrigger.ApexTrigger': {
    name: 'BillCodeTrigger',
    object: 'Bill Code (Custom)',
    overview:
      'Trigger on custom Bill Code object (likely Certinia/FinancialForce related). Specific logic not visible but handles billing code lifecycle events. Part of financial/billing automation.',
    serviceNowConcept:
      'Business Rule on custom billing code table. Depends on what billing/finance module is used in target. May integrate with ServiceNow Finance or remain custom.',
    migrationConsiderations: [
      'Medium complexity. Depends on billing/finance architecture decision. If Certinia → need to evaluate equivalent functionality. Custom billing table likely needed.',
    ],
  },
  'CAcResponsibility.ApexTrigger': {
    name: 'CAcResponsibility',
    object: 'IME_SC_CAc_Responsibility__c',
    overview:
      'Before insert/update trigger that prevents duplicate Responsibility records. Calls CAcResponsibilityService.preventDuplicateOnUpdateInsert(). Simple duplicate prevention pattern for customer account responsibility assignments.',
    serviceNowConcept:
      'Business Rule (before insert/update) with duplicate checking script. Or use Duplicate Detection Rules if available. Query for existing records with same key fields, abort if found.',
    migrationConsiderations: [
      'Low complexity. Standard duplicate prevention. Identify unique key fields. Consider: Business Rule vs. built-in duplicate detection feature.',
    ],
  },
  'CampaignCurrenyUpdate.ApexTrigger': {
    name: 'CampaignCurrenyUpdate',
    object: 'Campaign',
    overview:
      'Before insert/update trigger that auto-sets Campaign currency to USD when owner is from IME, IMLA, or IMAP divisions. Ensures consistent currency for international divisions. SOQL query on User to check Division.',
    serviceNowConcept:
      "Business Rule (before insert/update) on campaign table. Check user's department/division, set currency field. Consider: should this be UI Policy (client-side) or Business Rule (server-side)?",
    migrationConsiderations: [
      'Low complexity. Simple field defaulting based on user attribute. Map Division → Department or custom user field. Currency field mapping needed. Note: SOQL in loop is anti-pattern - ServiceNow handles differently.',
    ],
  },
  'CaseCommentObject.ApexTrigger': {
    name: 'CaseCommentObject',
    object: 'CaseComment',
    overview:
      'After insert trigger on Case Comments. Creates notification Task for Case owner when comment added (if not by owner). Prevents comments on closed Cases. Same pattern as AttachmentObject trigger. Uses custom settings for activation control.',
    serviceNowConcept:
      'Business Rule on sys_journal_input or case comments table. Notification to case owner on new comment. Condition: case not closed, commenter != owner. Consider Notification instead of Task creation.',
    migrationConsiderations: [
      'Medium complexity. Case comment model may differ. ServiceNow uses journal fields (work notes, comments) vs. separate object. Notification pattern same consideration as AttachmentObject.',
    ],
  },
};
